/**
 * Sprint S13.3.A — pricing page (public).
 *
 * Founding/standard tiers, founding-remaining counter, currency picker
 * (display only — Stripe Checkout auto-detects the customer's actual currency
 * at checkout time). "Subscribe" → POST /billing/checkout-session → redirect
 * to Stripe.
 *
 * Day 12: annual billing scope dropped (was misleading — "Pay annually" UX
 * promised a single £290 upfront + 12-month commit but the implementation
 * was monthly billing + 16.67% coupon). Future restoration would need true
 * annual Stripe Prices (£290/year single charge).
 *
 * Conventions: default export; `api` from @/api/client (baseURL already
 * /api, paths omit the prefix); i18n billing.* keys; project Tailwind
 * tokens (cream/navy/amber/taupe — named shades, no numeric scale).
 * Functional strawman pending runtime design review.
 */
import { LoadError } from "@/components/ui/LoadError";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, PAYWALL_REASON_KEY } from "@/api/client";

interface FoundingCounter {
  founding_active: number;
  founding_cap: number;
  founding_remaining: number;
  next_tier: "founding" | "standard";
}

const PRICES = {
  founding: { GBP: "£29", EUR: "€35", USD: "$39" },
  standard: { GBP: "£39", EUR: "€44", USD: "$49" },
} as const;

export default function PricingPage() {
  const { t } = useTranslation();
  // How the athlete got here: fresh from signup ("welcome"), bounced by the
  // 402 interceptor (the reason it stashed), or straight off the marketing
  // site (null — no banner, just the prices).
  const [arrival] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    if (new URLSearchParams(window.location.search).get("welcome") === "1") {
      sessionStorage.removeItem(PAYWALL_REASON_KEY);
      return "welcome";
    }
    const reason = sessionStorage.getItem(PAYWALL_REASON_KEY);
    if (reason) sessionStorage.removeItem(PAYWALL_REASON_KEY);
    return reason;
  });
  const [counter, setCounter] = useState<FoundingCounter | null>(null);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState<"GBP" | "EUR" | "USD">("GBP");
  const [error, setError] = useState<string | null>(null);

  const [counterError, setCounterError] = useState(false);
  const [counterAttempt, setCounterAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCounterError(false);
    api
      .get<FoundingCounter>("/billing/founding-counter", { signal: controller.signal })
      .then((res) => setCounter(res.data))
      .catch(() => { if (!controller.signal.aborted) setCounterError(true); });
    return () => controller.abort();
  }, [counterAttempt]);

  async function handleSubscribe(tier: "founding" | "standard") {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ url: string; session_id: string }>(
        "/billing/checkout-session",
        { tier, currency },
      );
      window.location.href = res.data.url;
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || t("billing.checkout_error");
      setError(detail);
      setLoading(false);
    }
  }

  if (counterError) {
    return <div className="mx-auto max-w-4xl px-5 py-10">
      <LoadError onRetry={() => setCounterAttempt((attempt) => attempt + 1)} />
    </div>;
  }

  if (!counter) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-navy">
        {t("billing.loading")}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      {/* Why the athlete is here. Landing on a pricing table with no
          explanation reads as a paywall slammed shut; one line naming the
          reason is the difference between "start your trial" and "you have
          been cut off". */}
      {arrival === "welcome" ? (
        <div className="mb-6 rounded-lg border border-amber bg-amber/10 px-4 py-3 text-[14px] text-navy">
          <p className="font-medium">{t("billing.paywall.welcome_title")}</p>
          <p className="mt-1 text-taupe">{t("billing.paywall.welcome_body")}</p>
        </div>
      ) : arrival ? (
        <div className="mb-6 rounded-lg border border-coral bg-coral/10 px-4 py-3 text-[14px] text-navy">
          <p className="font-medium">{t("billing.paywall.locked_title")}</p>
          <p className="mt-1 text-taupe">
            {t(`billing.paywall.reason.${arrival}`, {
              defaultValue: t("billing.paywall.reason.no_subscription"),
            })}
          </p>
        </div>
      ) : null}

      <h1 className="font-serif text-3xl font-medium tracking-tight text-navy">
        {t("billing.pricing.title")}
      </h1>
      <p className="mt-2 text-[15px] text-taupe">
        {arrival === "welcome"
          ? t("billing.paywall.trial_subtitle")
          : t("billing.pricing.subtitle")}
      </p>

      <div className="mt-6 flex items-center gap-3">
        <span className="text-sm text-taupe">
          {t("billing.pricing.currency_label")}:
        </span>
        {(["GBP", "EUR", "USD"] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCurrency(c)}
            className={`rounded-md px-3 py-1 text-sm ${
              currency === c
                ? "bg-amber text-navy-deep"
                : "bg-cream text-navy hover:bg-cream-light"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {counter.founding_remaining > 0 && (
          <div className="rounded-lg border-2 border-amber bg-cream p-6">
            <div className="mb-2 text-xs font-semibold uppercase text-amber">
              {t("billing.pricing.founding_label")}
            </div>
            <h2 className="text-2xl font-bold text-navy">
              {t("billing.pricing.founding_title")}
            </h2>
            <div className="mb-4 mt-1 text-sm text-taupe">
              {t("billing.pricing.founding_remaining", {
                count: counter.founding_remaining,
              })}
            </div>
            <div className="mb-4 text-4xl font-bold text-navy">
              {PRICES.founding[currency]}
              <span className="text-base font-normal text-taupe">
                /{t("billing.pricing.month")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleSubscribe("founding")}
              disabled={loading}
              className="w-full rounded-md bg-amber py-3 font-bold text-navy-deep hover:bg-amber-dark hover:text-cream-light disabled:opacity-50"
            >
              {loading
                ? t("billing.pricing.loading")
                : t("billing.pricing.subscribe_founding")}
            </button>
          </div>
        )}

        <div className="rounded-lg border border-cream-border bg-cream-light p-6">
          <h2 className="text-2xl font-bold text-navy">
            {t("billing.pricing.standard_title")}
          </h2>
          <div className="mb-4 mt-1 text-sm text-taupe">
            {t("billing.pricing.standard_description")}
          </div>
          <div className="mb-4 text-4xl font-bold text-navy">
            {PRICES.standard[currency]}
            <span className="text-base font-normal text-taupe">
              /{t("billing.pricing.month")}
            </span>
          </div>
          <button
            type="button"
            onClick={() => handleSubscribe("standard")}
            disabled={loading}
            className="w-full rounded-md bg-navy py-3 text-white hover:bg-navy-deep disabled:opacity-50"
          >
            {loading
              ? t("billing.pricing.loading")
              : t("billing.pricing.subscribe_standard")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-coral p-4 text-coral">
          {error}
        </div>
      )}
    </div>
  );
}
